import {
  groupedRuleCriteriaSchema,
  hasGroupedRuleCriteria,
  supportsTariffExclusions,
} from '@billing/validation/rules';
import type { GroupedRuleCriteria } from '@billing/validation/rules';
import { DateTime } from 'luxon';
import { isRecord } from 'utils/objectUtils';
import {
  ScopeMode,
  AssertionField,
  AssertionOperator,
  RuleSeverity,
  RuleMode,
  WhenLogic,
  ServiceStartTimeOperator,
  ServiceStartDayOperator,
  PatientAgeOperator,
} from '@billing/validation/rules';
import { tariffBillingHistoryParamsSchema } from '@billing/validation/rules';
import type { TariffBillingHistoryWindow } from '@billing/validation/rules';
import {
  assertionSummaryOperatorKeys,
  billingHistoryPeriodOptions,
  historyCriteriaFields,
  categoricalDemographicScopeModes,
  tariffScopeModes,
  claimCriteriaFields,
  claimCriteriaOperatorsByInput,
  whenRangeOperators,
  demographicFactByScopeMode,
  assertionOperators,
  timeBasedApplyFields,
  TIME_BASED_INTERVALS,
  timeAssertionOperators,
  dateAssertionOperators,
  defaultAssertionOperators,
  stringContainsAssertionFields,
  stringAssertionOperators,
  ageAssertionOperators,
  categoricalDemographicAssertionOperators,
} from './constants';
import type {
  ClaimCriteriaField,
  ClaimCriteriaInput,
  RuleCondition,
  RuleCriteriaGroup,
  RuleSummaryContext,
  RuleSummaryFormatters,
  RuleTranslate,
} from './constants';
import {
  getRuleClaimFieldOptions,
  getRuleClaimOptionValue,
} from './ruleClaimFieldOptions';
import {
  applyUpdateRowSchema,
  assertionRowSchema,
  savedApplyPayloadSchema,
  savedApplyUpdateSchema,
  savedAssertionRowSchema,
  savedRuleMetadataSchema,
  savedWhenClauseSchema,
  whenClauseSchema,
} from './ruleSchemas';
import type {
  ApplyUpdateRow,
  AssertionRow,
  BillingHistoryState,
  RuleModeSelection,
  SavedRuleMetadata,
  WhenClause,
  WhenOperator,
} from './ruleSchemas';

export const createEmptyAssertion = (ID: number): AssertionRow =>
  assertionRowSchema.parse({ id: ID });

export const createEmptyApplyUpdate = (ID: number): ApplyUpdateRow =>
  applyUpdateRowSchema.parse({ id: ID, writePolicy: 'blank' });

export const createEmptyWhenClause = (ID: number): WhenClause =>
  whenClauseSchema.parse({ id: ID });

/** Groups saved checks separately because they always use OR, independent of Match All/Any. */
export const getRuleCriteriaGroups = (
  clauses: WhenClause[],
  logic: WhenLogic
): RuleCriteriaGroup[] => {
  const rows = clauses.map((clause, index) => ({ clause, index }));
  const groups: RuleCriteriaGroup[] = [
    {
      key: 'criteria',
      logic,
      rows: rows.filter(({ clause }) => !clause.legacyCheckGroup),
    },
    {
      key: 'savedChecks',
      logic: WhenLogic.Any,
      headingKey: 'pages:rules.historyCriteria.savedChecks',
      rows: rows.filter(({ clause }) => clause.legacyCheckGroup),
    },
  ];
  return groups
    .filter((group) => group.rows.length > 0)
    .map((group, groupIndex) => ({
      ...group,
      rows: group.rows.map((row, rowIndex) => {
        if (rowIndex === 0)
          return {
            ...row,
            joinerKey:
              groupIndex === 0
                ? 'pages:rules.editControls.when'
                : 'pages:rules.editControls.and',
          };
        return {
          ...row,
          joinerKey:
            group.logic === WhenLogic.All
              ? 'pages:rules.editControls.and'
              : 'pages:rules.editControls.or',
        };
      }),
    }));
};

export const getBillingHistoryWindow = (
  history: BillingHistoryState
): TariffBillingHistoryWindow => {
  if (history.windowType === 'rolling') {
    return {
      type: 'rolling',
      amount: Number(history.amount),
      unit: history.unit,
    };
  }
  return { type: history.windowType };
};

/** Retains custom rolling windows from saved rules alongside the design's fixed periods. */
export const getBillingHistoryPeriod = (
  history: BillingHistoryState
): string => {
  if (history.windowType !== 'rolling') return history.windowType;
  return history.amount === '30' && history.unit === 'days'
    ? 'last30Days'
    : 'rolling';
};

export const getBillingHistoryPeriodUpdate = (
  value: string
): Partial<BillingHistoryState> => {
  if (value === 'last30Days')
    return { windowType: 'rolling', amount: '30', unit: 'days' };
  if (value === 'rolling') return { windowType: 'rolling' };
  const option = billingHistoryPeriodOptions.find(
    (period) => period.value === value
  );
  if (!option || option.value === 'last30Days') return {};
  return { windowType: option.value };
};

const buildBillingHistoryCondition = (
  tariffCodes: string[],
  history: BillingHistoryState
): RuleCondition | null => {
  const params = tariffBillingHistoryParamsSchema.safeParse({
    tariffCodes,
    window: getBillingHistoryWindow(history),
    sameProvider: history.sameProvider,
  });
  if (!params.success) return null;
  return {
    fact: 'hasPriorTariffBilling',
    operator: 'equal',
    value: true,
    params: params.data,
  };
};

// Field classification and supported operators

/** Resolves claim-specific behavior without maintaining parallel field lists. */
export const getClaimCriteriaField = (
  scopeMode: ScopeMode
): ClaimCriteriaField | undefined =>
  claimCriteriaFields.find((field) => field.scopeMode === scopeMode);

/** Shares the same operator choices between the controls and submission validation. */
export const getClaimCriteriaOperators = (
  scopeMode: ScopeMode
): WhenOperator[] =>
  claimCriteriaOperatorsByInput[
    getClaimCriteriaField(scopeMode)?.input ?? 'identifier'
  ];

/** Keeps range detection consistent when rendering, editing, describing, and compiling values. */
export const isWhenRangeOperator = (operator: WhenOperator): boolean =>
  whenRangeOperators.includes(operator);

export const isTimeField = (field: AssertionField): boolean =>
  field === AssertionField.ClaimServiceStartMinutesLocal;

export const isDayField = (field: AssertionField): boolean =>
  field === AssertionField.ClaimServiceStartDayOfWeekLocal;

const isDateField = (field: AssertionField): boolean =>
  field === AssertionField.ServiceStart || field === AssertionField.ServiceEnd;

export const isAgeAssertionField = (field: AssertionField): boolean =>
  field === AssertionField.PatientAge;

export const isCategoricalDemographicAssertionField = (
  field: AssertionField
): boolean =>
  field === AssertionField.PatientProvince ||
  field === AssertionField.PatientSex ||
  field === AssertionField.PatientMaritalStatus;

const isDemographicAssertionField = (field: AssertionField): boolean =>
  isAgeAssertionField(field) || isCategoricalDemographicAssertionField(field);

export const operatorsForField = (
  field: AssertionField
): AssertionOperator[] => {
  if (isAgeAssertionField(field)) return ageAssertionOperators;
  if (isCategoricalDemographicAssertionField(field)) {
    return categoricalDemographicAssertionOperators;
  }
  if (isTimeField(field) || isDayField(field)) return timeAssertionOperators;
  if (isDateField(field)) return dateAssertionOperators;
  if (stringContainsAssertionFields.has(field)) return stringAssertionOperators;
  if (field === AssertionField.PatientCount) return assertionOperators;
  return defaultAssertionOperators;
};

export const usesRangeOperator = (
  operator: AssertionOperator
): operator is
  | AssertionOperator.BetweenInclusive
  | AssertionOperator.NotBetweenInclusive =>
  operator === AssertionOperator.BetweenInclusive ||
  operator === AssertionOperator.NotBetweenInclusive;

// Value parsing and display

export const parseValueList = (raw: string): string[] =>
  raw
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

export const normalizeExactTariffValues = (raw: string): string[] =>
  Array.from(new Set(parseValueList(raw)));

const toLiteralValue = (value: string): unknown => {
  const normalized = value.trim();
  const lower = normalized.toLowerCase();
  if (lower === 'true') return true;
  if (lower === 'false') return false;
  const numberValue = Number(normalized);
  if (normalized.length > 0 && Number.isFinite(numberValue)) return numberValue;
  return normalized;
};

export const hhmmToMinutes = (value: string): number | null => {
  const trimmed = value.trim();
  const parts = trimmed.split(':');
  if (parts.length !== 2) return null;
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
};

export const hhmmRangeToMinutes = (
  startValue: string,
  endValue: string
): [number, number] | null => {
  const startMinutes = hhmmToMinutes(startValue);
  const endMinutes = hhmmToMinutes(endValue);
  if (startMinutes === null || endMinutes === null) return null;

  // Clerks commonly intend "7 PM to 12:59" as "7 PM to end of day".
  // Native time inputs serialize that as 12:59, so normalize this case.
  if (startMinutes >= 12 * 60 && endValue.trim() === '12:59') {
    return [startMinutes, 23 * 60 + 59];
  }

  return [startMinutes, endMinutes];
};

export const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Criteria comparison controls

export const getWhenComparisonValue = (clause: WhenClause): string => {
  if (clause.whenOperator === 'exists' || clause.whenOperator === 'notExists') {
    return clause.whenOperator;
  }
  if (clause.scopeMode === ScopeMode.PatientAge) {
    if (clause.whenOperator !== 'isNot') return clause.patientAgeOperator;
    const inverted: Record<PatientAgeOperator, string> = {
      [PatientAgeOperator.Equal]: 'notEqual',
      [PatientAgeOperator.LessThan]: PatientAgeOperator.GreaterThanInclusive,
      [PatientAgeOperator.LessThanInclusive]: PatientAgeOperator.GreaterThan,
      [PatientAgeOperator.GreaterThan]: PatientAgeOperator.LessThanInclusive,
      [PatientAgeOperator.GreaterThanInclusive]: PatientAgeOperator.LessThan,
      [PatientAgeOperator.BetweenInclusive]: 'notBetween',
    };
    return inverted[clause.patientAgeOperator];
  }
  const operator =
    clause.scopeMode === ScopeMode.ServiceStartTimeLocal
      ? clause.serviceStartTimeOperator
      : clause.serviceStartDayOperator;
  if (clause.whenOperator !== 'isNot') return operator;
  if (operator === ServiceStartTimeOperator.Equal) return 'notEqual';
  return operator === ServiceStartTimeOperator.BetweenInclusive
    ? ServiceStartTimeOperator.NotBetweenInclusive
    : ServiceStartTimeOperator.BetweenInclusive;
};

export const getWhenComparisonUpdate = (
  scopeMode: ScopeMode,
  value: string
): Partial<WhenClause> => {
  if (value === 'exists' || value === 'notExists')
    return { whenOperator: value };
  if (scopeMode === ScopeMode.PatientAge) {
    if (value === 'notEqual')
      return {
        whenOperator: 'isNot',
        patientAgeOperator: PatientAgeOperator.Equal,
      };
    if (value === 'notBetween')
      return {
        whenOperator: 'isNot',
        patientAgeOperator: PatientAgeOperator.BetweenInclusive,
      };
    const patientAgeOperator = Object.values(PatientAgeOperator).find(
      (operator) => operator === value
    );
    return patientAgeOperator ? { whenOperator: 'is', patientAgeOperator } : {};
  }
  if (scopeMode === ScopeMode.ServiceStartTimeLocal) {
    if (value === 'notEqual')
      return {
        whenOperator: 'isNot',
        serviceStartTimeOperator: ServiceStartTimeOperator.Equal,
      };
    const serviceStartTimeOperator = Object.values(
      ServiceStartTimeOperator
    ).find((operator) => operator === value);
    return serviceStartTimeOperator
      ? { whenOperator: 'is', serviceStartTimeOperator }
      : {};
  }
  if (scopeMode === ScopeMode.ServiceStartDayLocal) {
    if (value === 'notEqual')
      return {
        whenOperator: 'isNot',
        serviceStartDayOperator: ServiceStartDayOperator.Equal,
      };
    const serviceStartDayOperator = Object.values(ServiceStartDayOperator).find(
      (operator) => operator === value
    );
    return serviceStartDayOperator
      ? { whenOperator: 'is', serviceStartDayOperator }
      : {};
  }
  return {};
};

// Saved rule initialization

const readSavedRuleMetadata = (
  body?: Record<string, unknown>
): SavedRuleMetadata => {
  const parsed = savedRuleMetadataSchema.safeParse(body);
  return parsed.success ? parsed.data : savedRuleMetadataSchema.parse({});
};

export const getBodyEventParams = (
  body?: Record<string, unknown>
): Record<string, unknown> => readSavedRuleMetadata(body).event?.params ?? {};

export const getBodyApplyPayload = (
  body?: Record<string, unknown>
): Record<string, unknown> => {
  const parsed = savedApplyPayloadSchema.safeParse(
    getBodyEventParams(body).apply
  );
  return parsed.success ? parsed.data : {};
};

export const getInitialRuleModeSelection = (
  body?: Record<string, unknown>
): RuleModeSelection => {
  const action = getBodyEventParams(body).action;
  switch (action) {
    case 'flagOnly':
      return 'flagOnly';
    case RuleMode.ApplyData:
      return RuleMode.ApplyData;
    case 'applyTariffTransforms':
      return RuleMode.TariffTransforms;
    default:
      return RuleMode.Validate;
  }
};

export const getInitialSeverity = (
  body?: Record<string, unknown>
): RuleSeverity => {
  const params = getBodyEventParams(body);
  if (params.severity === RuleSeverity.Alert || params.code === 'RULE_ALERT') {
    return RuleSeverity.Alert;
  }
  return RuleSeverity.Block;
};

export const getInitialWhenLogic = (
  body?: Record<string, unknown>
): WhenLogic => readSavedRuleMetadata(body).when?.operator ?? WhenLogic.All;

export const getInitialWhenClauses = (
  body?: Record<string, unknown>
): WhenClause[] => {
  if (hasGroupedRuleCriteria(body)) {
    return (
      groupedRuleCriteriaSchema
        .safeParse(body?.when)
        .data?.groups.flatMap((group) => group.clauses) ?? []
    );
  }
  const clauses = readSavedRuleMetadata(body).when?.clauses;
  if (!clauses?.length) return [createEmptyWhenClause(1)];
  return clauses.map((rawClause, index) => {
    const parsed = savedWhenClauseSchema.safeParse(rawClause);
    const clause = parsed.success
      ? parsed.data
      : savedWhenClauseSchema.parse({});
    return { ...clause, id: clause.id ?? index + 1 };
  });
};

export const getInitialApplyUpdates = (
  body?: Record<string, unknown>
): ApplyUpdateRow[] => {
  const apply = getBodyApplyPayload(body);
  const parsed = savedApplyPayloadSchema.parse(apply);
  const updates = (parsed.updates ?? []).flatMap(
    (rawUpdate, index): ApplyUpdateRow[] => {
      if (
        !isRecord(rawUpdate) ||
        typeof rawUpdate.field !== 'string' ||
        !rawUpdate.field.trim()
      )
        return [];
      const value =
        timeBasedApplyFields.has(rawUpdate.field) && isRecord(rawUpdate.value)
          ? { ...rawUpdate, value: '' }
          : rawUpdate;
      const result = savedApplyUpdateSchema.safeParse(value);
      if (!result.success) return [];
      return [
        {
          ...result.data,
          field: rawUpdate.field.trim(),
          id: result.data.id ?? index + 1,
        },
      ];
    }
  );
  if (updates.length) return updates;
  const primary = savedApplyUpdateSchema.safeParse(apply);
  return [
    {
      ...(primary.success ? primary.data : savedApplyUpdateSchema.parse({})),
      id: 1,
    },
  ];
};

/** Reads compiled time-based updates first, with a fallback for older primary apply payloads. */
export const getInitialApplyIntervalMinutes = (
  body?: Record<string, unknown>
): string => {
  const apply = getBodyApplyPayload(body);
  const update = Array.isArray(apply.updates)
    ? apply.updates.find(
        (row) =>
          isRecord(row) &&
          typeof row.field === 'string' &&
          timeBasedApplyFields.has(row.field) &&
          isRecord(row.value)
      )
    : undefined;
  const interval =
    isRecord(update) && isRecord(update.value)
      ? update.value.intervalMinutes
      : apply.intervalMinutes;
  if (
    typeof interval === 'number' &&
    TIME_BASED_INTERVALS.some((value) => value === String(interval))
  )
    return String(interval);
  return TIME_BASED_INTERVALS[0];
};

export const getInitialAssertions = (
  body?: Record<string, unknown>
): AssertionRow[] => {
  const metadata = readSavedRuleMetadata(body);
  const checks = metadata.checks;
  const parsedChecks = (checks ?? []).map((rawCheck, index) => {
    const parsed = savedAssertionRowSchema.safeParse(rawCheck);
    const check = parsed.success
      ? parsed.data
      : savedAssertionRowSchema.parse({});
    return { ...check, id: check.id ?? index + 1 };
  });
  const history = metadata.billingHistory;
  if (
    history?.enabled &&
    !parsedChecks.some(
      (check) => check.field === AssertionField.TariffBillingHistory
    )
  ) {
    parsedChecks.push({
      ...savedAssertionRowSchema.parse({}),
      id: parsedChecks.length + 1,
      field: AssertionField.TariffBillingHistory,
      billingHistory: {
        windowType: history.window.type,
        amount: history.window.amount,
        unit: history.window.unit,
        sameProvider: history.sameProvider,
      },
    });
  }
  return parsedChecks;
};

/** Reads grouped metadata first; legacy checks retain their fixed OR relationship. */
export const getInitialGroupedCriteria = (
  body?: Record<string, unknown>
): GroupedRuleCriteria | null => {
  if (hasGroupedRuleCriteria(body)) {
    const result = groupedRuleCriteriaSchema.safeParse(body?.when);
    return result.success ? result.data : null;
  }
  const initial = getInitialRuleCriteria(body).whenClauses;
  const usedIDs = new Set<number>();
  let nextID =
    Math.max(
      0,
      ...initial.map((clause) => (Number.isInteger(clause.id) ? clause.id : 0))
    ) + 1;
  const whenClauses = initial.map((clause) => {
    const clauseID =
      Number.isInteger(clause.id) && clause.id > 0 && !usedIDs.has(clause.id)
        ? clause.id
        : nextID++;
    usedIDs.add(clauseID);
    return { ...clause, id: clauseID };
  });
  const groups = getRuleCriteriaGroups(
    whenClauses,
    getInitialWhenLogic(body)
  ).map((group, index) => ({
    id: index + 1,
    operator: group.logic,
    clauses: group.rows.map((row) => row.clause),
  }));
  return {
    operator: WhenLogic.All,
    groups: body
      ? groups
      : [
          {
            id: 1,
            operator: WhenLogic.Any,
            clauses: [createEmptyWhenClause(1)],
          },
        ],
  };
};

// Criteria condition construction

/** Moves optional legacy checks into visible criteria while preserving their fixed OR group. */
export const getInitialRuleCriteria = (
  body?: Record<string, unknown>
): { whenClauses: WhenClause[]; assertions: AssertionRow[] } => {
  const whenClauses = getInitialWhenClauses(body);
  const assertions = getInitialAssertions(body);
  if (hasGroupedRuleCriteria(body)) return { whenClauses, assertions };
  if (getInitialRuleModeSelection(body) !== RuleMode.Validate)
    return { whenClauses, assertions };
  const tariffs = whenClauses
    .filter(
      (clause) =>
        clause.scopeMode === ScopeMode.ExactTariff &&
        clause.whenOperator === 'is'
    )
    .flatMap((clause) => normalizeExactTariffValues(clause.scopeValuesText));
  const nextID = Math.max(...whenClauses.map((clause) => clause.id), 0) + 1;
  return {
    whenClauses: [
      ...whenClauses,
      ...assertions.flatMap((check, index) => {
        const field = historyCriteriaFields.find(
          (definition) => definition.assertionField === check.field
        );
        if (!field) return [];
        return [
          {
            ...createEmptyWhenClause(nextID + index),
            scopeMode: field.scopeMode,
            whenOperator: field.operator,
            scopeValuesText:
              field.scopeMode === ScopeMode.TariffBillingHistory
                ? tariffs.join(', ')
                : '',
            billingHistory: check.billingHistory,
            legacyCheckGroup: true,
          },
        ];
      }),
    ],
    assertions: assertions.filter(
      (check) =>
        !historyCriteriaFields.some(
          (field) => field.assertionField === check.field
        )
    ),
  };
};

/** Negates compound conditions while retaining the rule engine's supported structure. */
const negateCondition = (condition: RuleCondition): RuleCondition => {
  if ('all' in condition) {
    const conditions = condition.all;
    return Array.isArray(conditions)
      ? {
          any: conditions.map((item) => negateCondition(item as RuleCondition)),
        }
      : { not: condition };
  }

  if ('any' in condition) {
    const conditions = condition.any;
    return Array.isArray(conditions)
      ? {
          all: conditions.map((item) => negateCondition(item as RuleCondition)),
        }
      : { not: condition };
  }

  return { not: condition };
};

/** Builds claim comparisons with shared presence guards, including site-local calendar dates. */
const buildClaimCriteriaCondition = (
  clause: WhenClause,
  timezone: string
): RuleCondition | null => {
  const field = getClaimCriteriaField(clause.scopeMode);
  if (
    !field ||
    !getClaimCriteriaOperators(clause.scopeMode).includes(clause.whenOperator)
  )
    return null;
  const { fact, input } = field;
  const exists: RuleCondition = { fact, operator: 'mustExist', value: true };
  const present: RuleCondition =
    fact === AssertionField.ReferringProvider
      ? { all: [exists, { not: { fact, operator: 'equal', value: '00000' } }] }
      : exists;
  if (clause.whenOperator === 'exists') return present;
  if (clause.whenOperator === 'notExists') return negateCondition(present);
  const rawValues = clause.scopeValuesText
    .split(',')
    .map((value) => value.trim());
  const isRange = isWhenRangeOperator(clause.whenOperator);
  const negated =
    clause.whenOperator === 'isNot' ||
    clause.whenOperator === 'notBetween' ||
    clause.whenOperator === 'notContains';
  const guard = (comparison: RuleCondition): RuleCondition => ({
    all: [present, negated ? negateCondition(comparison) : comparison],
  });

  if (input === 'select' || input === 'identifier') {
    const values = rawValues.filter(Boolean);
    return values.length > 0
      ? guard({ fact, operator: 'in', value: values })
      : null;
  }

  if (input === 'number') {
    if (
      rawValues.length !== (isRange ? 2 : 1) ||
      rawValues.some((value) => !/^\d+$/.test(value))
    )
      return null;
    const values = rawValues.map(Number);
    if (values.some((value) => !Number.isSafeInteger(value))) return null;
    if (isRange) {
      if (values[0] > values[1]) return null;
      return guard({ fact, operator: 'betweenInclusive', value: values });
    }
    const operator =
      clause.whenOperator === 'is' || clause.whenOperator === 'isNot'
        ? 'equal'
        : clause.whenOperator;
    return guard({ fact, operator, value: values[0] });
  }

  if (input === 'date') {
    if (rawValues.length !== (isRange ? 2 : 1)) return null;
    const dates = rawValues.map((value) =>
      DateTime.fromISO(value, { zone: timezone })
    );
    if (
      dates.some(
        (date, index) =>
          !/^\d{4}-\d{2}-\d{2}$/.test(rawValues[index]) ||
          !date.isValid ||
          date.toISODate() !== rawValues[index]
      )
    )
      return null;
    const start = dates[0].startOf('day');
    const end = dates[isRange ? 1 : 0].endOf('day');
    if (start.toMillis() > end.toMillis()) return null;
    if (clause.whenOperator === 'onOrAfter')
      return guard({
        fact,
        operator: 'dateOnOrAfter',
        value: start.toUTC().toISO(),
      });
    if (clause.whenOperator === 'onOrBefore')
      return guard({
        fact,
        operator: 'dateOnOrBefore',
        value: end.toUTC().toISO(),
      });
    return guard({
      fact,
      operator: 'dateBetweenInclusive',
      value: [start.toUTC().toISO(), end.toUTC().toISO()],
    });
  }

  if (input === 'text') {
    const value = clause.scopeValuesText.trim();
    if (!value) return null;
    const operator =
      clause.whenOperator === 'contains' ||
      clause.whenOperator === 'notContains'
        ? 'includesIgnoreCase'
        : 'equal';
    return guard({ fact, operator, value });
  }
  return null;
};

const buildScopeExistsCondition = (scopeMode: ScopeMode): RuleCondition => {
  if (scopeMode === ScopeMode.AnyClaim) {
    return {
      fact: 'alwaysTrue',
      operator: 'equal',
      value: true,
    };
  }

  if (
    scopeMode === ScopeMode.ServiceStartTimeLocal ||
    scopeMode === ScopeMode.ServiceStartDayLocal
  ) {
    return {
      fact:
        scopeMode === ScopeMode.ServiceStartTimeLocal
          ? 'claimServiceStartMinutesLocal'
          : 'claimServiceStartDayOfWeekLocal',
      operator: 'mustExist',
      value: true,
    };
  }

  const demographicFact = demographicFactByScopeMode[scopeMode];
  if (demographicFact) {
    return {
      fact: demographicFact,
      operator: 'mustExist',
      value: true,
    };
  }

  if (scopeMode === ScopeMode.SpecialtyCode) {
    return negateCondition({
      fact: 'tariffSpecialtyCodes',
      operator: 'hasLength',
      value: 0,
    });
  }

  if (scopeMode === ScopeMode.TariffHasAnestheticUnitValue) {
    return {
      fact: 'hasTariffAnestheticUnitValue',
      operator: 'equal',
      value: true,
    };
  }

  return negateCondition({
    fact: 'tariffFullCodes',
    operator: 'hasLength',
    value: 0,
  });
};

const applyWhenOperator = (
  condition: RuleCondition | null,
  whenOperator: WhenOperator,
  scopeMode: ScopeMode
): RuleCondition | null => {
  if (whenOperator === 'exists') {
    return buildScopeExistsCondition(scopeMode);
  }

  if (whenOperator === 'notExists') {
    return negateCondition(buildScopeExistsCondition(scopeMode));
  }

  if (!condition) return null;
  return whenOperator === 'isNot' ? negateCondition(condition) : condition;
};

const buildScopeCondition = (
  scopeMode: ScopeMode,
  scopeValues: string[],
  tariffParams?: { excludedTariffs: string[] }
): RuleCondition | null => {
  if (scopeMode === ScopeMode.AnyClaim) {
    return {
      fact: 'alwaysTrue',
      operator: 'equal',
      value: true,
    };
  }

  if (
    scopeMode === ScopeMode.ServiceStartTimeLocal ||
    scopeMode === ScopeMode.ServiceStartDayLocal
  ) {
    return null;
  }

  if (scopeMode === ScopeMode.SpecialtyCode) {
    if (scopeValues.length === 0) return null;
    return {
      fact: 'tariffSpecialtyCodes',
      operator: 'containsAny',
      value: scopeValues,
      ...(tariffParams ? { params: tariffParams } : {}),
    };
  }

  if (scopeMode === ScopeMode.TariffHasAnestheticUnitValue) {
    return {
      fact: 'hasTariffAnestheticUnitValue',
      operator: 'equal',
      value: true,
      ...(tariffParams ? { params: tariffParams } : {}),
    };
  }

  if (scopeValues.length === 0) return null;

  const conditionFactory = (value: string): RuleCondition => {
    if (scopeMode === ScopeMode.ExactTariff) {
      return {
        fact: 'hasTariff',
        operator: 'equal',
        value: true,
        params: { tariff: value },
      };
    }
    if (scopeMode === ScopeMode.TariffNotEqual) {
      return {
        fact: 'hasTariffCode',
        operator: 'equal',
        value: false,
        params: { code: value },
      };
    }
    if (scopeMode === ScopeMode.TariffCode) {
      return {
        fact: 'hasTariffCode',
        operator: 'equal',
        value: true,
        params: { code: value, ...tariffParams },
      };
    }
    return {
      fact: 'hasTariffPrefix',
      operator: 'equal',
      value: true,
      params: { prefix: value, ...tariffParams },
    };
  };

  if (scopeValues.length === 1) {
    return conditionFactory(scopeValues[0]);
  }

  return scopeMode === ScopeMode.TariffNotEqual
    ? { all: scopeValues.map((value) => conditionFactory(value)) }
    : { any: scopeValues.map((value) => conditionFactory(value)) };
};

export const buildWhenClauseCondition = (
  clause: WhenClause,
  timezone = 'UTC'
): RuleCondition | null => {
  const excludedTariffs = normalizeExactTariffValues(
    clause.excludedTariffValuesText ?? ''
  );
  if (
    clause.excludedTariffValuesText?.trim() &&
    (!supportsTariffExclusions(clause.scopeMode, clause.whenOperator) ||
      excludedTariffs.length === 0)
  )
    return null;

  if (clause.scopeMode === ScopeMode.DuplicateClaim) {
    if (clause.whenOperator !== 'exists') return null;
    return { fact: 'hasPriorDuplicateClaim', operator: 'equal', value: true };
  }
  if (clause.scopeMode === ScopeMode.TariffBillingHistory) {
    if (clause.whenOperator !== 'is') return null;
    return buildBillingHistoryCondition(
      parseValueList(clause.scopeValuesText),
      clause.billingHistory
    );
  }
  if (getClaimCriteriaField(clause.scopeMode)) {
    return buildClaimCriteriaCondition(clause, timezone);
  }
  if (!['is', 'isNot', 'exists', 'notExists'].includes(clause.whenOperator))
    return null;

  const scopeValues =
    clause.scopeMode === ScopeMode.ExactTariff
      ? normalizeExactTariffValues(clause.scopeValuesText)
      : parseValueList(clause.scopeValuesText);

  const demographicFact = demographicFactByScopeMode[clause.scopeMode];
  if (demographicFact) {
    if (clause.whenOperator === 'exists') {
      return {
        fact: demographicFact,
        operator: 'mustExist',
        value: true,
      };
    }
    if (clause.whenOperator === 'notExists') {
      return {
        fact: demographicFact,
        operator: 'mustExist',
        value: false,
      };
    }

    let comparison: RuleCondition | null = null;
    if (clause.scopeMode === ScopeMode.PatientAge) {
      const value = Number(clause.patientAgeValue);
      const start = Number(clause.patientAgeStart);
      const end = Number(clause.patientAgeEnd);
      const isValidAge = (age: number, raw: string) =>
        raw.trim().length > 0 && Number.isInteger(age) && age >= 0;

      if (clause.patientAgeOperator === PatientAgeOperator.BetweenInclusive) {
        if (
          !isValidAge(start, clause.patientAgeStart) ||
          !isValidAge(end, clause.patientAgeEnd) ||
          start > end
        ) {
          return null;
        }
        comparison = {
          fact: demographicFact,
          operator: PatientAgeOperator.BetweenInclusive,
          value: [start, end],
        };
      } else {
        if (!isValidAge(value, clause.patientAgeValue)) return null;
        comparison = {
          fact: demographicFact,
          operator: clause.patientAgeOperator,
          value,
        };
      }
    } else {
      if (scopeValues.length === 0) return null;
      comparison = {
        fact: demographicFact,
        operator: 'in',
        value: scopeValues,
      };
    }

    return {
      all: [
        {
          fact: demographicFact,
          operator: 'mustExist',
          value: true,
        },
        clause.whenOperator === 'isNot' ? { not: comparison } : comparison,
      ],
    };
  }

  if (clause.scopeMode === ScopeMode.ServiceStartTimeLocal) {
    if (
      clause.whenOperator === 'exists' ||
      clause.whenOperator === 'notExists'
    ) {
      return applyWhenOperator(null, clause.whenOperator, clause.scopeMode);
    }

    if (clause.serviceStartTimeOperator === ServiceStartTimeOperator.Equal) {
      const minutes = hhmmToMinutes(clause.serviceStartTimeValue);
      if (minutes === null) return null;
      return applyWhenOperator(
        {
          fact: 'claimServiceStartMinutesLocal',
          operator: 'equal',
          value: minutes,
        },
        clause.whenOperator,
        clause.scopeMode
      );
    }

    const rangeMinutes = hhmmRangeToMinutes(
      clause.serviceStartTimeStart,
      clause.serviceStartTimeEnd
    );
    if (!rangeMinutes) return null;
    return applyWhenOperator(
      {
        fact: 'claimServiceStartMinutesLocal',
        operator: clause.serviceStartTimeOperator,
        value: rangeMinutes,
      },
      clause.whenOperator,
      clause.scopeMode
    );
  }

  if (clause.scopeMode === ScopeMode.ServiceStartDayLocal) {
    const fact = 'claimServiceStartDayOfWeekLocal';

    if (
      clause.whenOperator === 'exists' ||
      clause.whenOperator === 'notExists'
    ) {
      return applyWhenOperator(null, clause.whenOperator, clause.scopeMode);
    }

    if (clause.serviceStartDayOperator === ServiceStartDayOperator.Equal) {
      return applyWhenOperator(
        {
          fact,
          operator: 'equal',
          value: Number(clause.serviceStartDayValue),
        },
        clause.whenOperator,
        clause.scopeMode
      );
    }

    const start = Number(clause.serviceStartDayStart);
    const end = Number(clause.serviceStartDayEnd);
    if (!Number.isInteger(start) || !Number.isInteger(end)) return null;

    if (start <= end) {
      return applyWhenOperator(
        {
          fact,
          operator: clause.serviceStartDayOperator,
          value: [start, end],
        },
        clause.whenOperator,
        clause.scopeMode
      );
    }

    if (
      clause.serviceStartDayOperator ===
      ServiceStartDayOperator.BetweenInclusive
    ) {
      return applyWhenOperator(
        {
          any: [
            { fact, operator: 'betweenInclusive', value: [start, 6] },
            { fact, operator: 'betweenInclusive', value: [0, end] },
          ],
        },
        clause.whenOperator,
        clause.scopeMode
      );
    }

    return applyWhenOperator(
      {
        fact,
        operator: 'betweenExclusive',
        value: [end, start],
      },
      clause.whenOperator,
      clause.scopeMode
    );
  }

  return applyWhenOperator(
    buildScopeCondition(
      clause.scopeMode,
      scopeValues,
      excludedTariffs.length > 0 ? { excludedTariffs } : undefined
    ),
    clause.whenOperator,
    clause.scopeMode
  );
};

// Existing validation check conditions

const buildDayRangeCondition = (
  fact: string,
  operator:
    | AssertionOperator.BetweenInclusive
    | AssertionOperator.NotBetweenInclusive,
  start: number,
  end: number
): RuleCondition => {
  if (start <= end) {
    return {
      fact,
      operator,
      value: [start, end],
    };
  }

  if (operator === AssertionOperator.BetweenInclusive) {
    return {
      any: [
        { fact, operator: 'betweenInclusive', value: [start, 6] },
        { fact, operator: 'betweenInclusive', value: [0, end] },
      ],
    };
  }

  return {
    fact,
    operator: 'betweenExclusive',
    value: [end, start],
  };
};

export const buildAssertionCondition = (
  assertion: AssertionRow
): RuleCondition | null => {
  if (assertion.operator === AssertionOperator.MustExist) {
    return {
      fact: assertion.field,
      operator: 'mustExist',
      value: false,
    };
  }

  if (assertion.operator === AssertionOperator.Contains) {
    const value = assertion.valuesText.trim();
    if (!value) return null;
    return {
      fact: assertion.field,
      operator: 'includesIgnoreCase',
      value,
    };
  }

  const values = parseValueList(assertion.valuesText);
  if (values.length === 0) return null;
  const asValue = (raw: string) =>
    isTimeField(assertion.field) ? hhmmToMinutes(raw) : toLiteralValue(raw);

  if (isAgeAssertionField(assertion.field)) {
    const ages = values.map(Number);
    if (ages.some((age) => !Number.isInteger(age) || age < 0)) return null;

    if (usesRangeOperator(assertion.operator)) {
      if (ages.length < 2 || ages[0] > ages[1]) return null;
      return {
        fact: assertion.field,
        operator: assertion.operator,
        value: [ages[0], ages[1]],
      };
    }

    if (
      assertion.operator === AssertionOperator.LessThan ||
      assertion.operator === AssertionOperator.LessThanInclusive ||
      assertion.operator === AssertionOperator.GreaterThan ||
      assertion.operator === AssertionOperator.GreaterThanInclusive
    ) {
      return {
        fact: assertion.field,
        operator: assertion.operator,
        value: ages[0],
      };
    }
  }

  if (isDayField(assertion.field)) {
    if (assertion.operator === AssertionOperator.Equals) {
      return {
        fact: assertion.field,
        operator: 'equal',
        value: Number(values[0]),
      };
    }

    if (usesRangeOperator(assertion.operator)) {
      if (values.length < 2) return null;
      const start = Number(values[0]);
      const end = Number(values[1]);
      if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
      return buildDayRangeCondition(
        assertion.field,
        assertion.operator,
        start,
        end
      );
    }
  }

  if (assertion.operator === AssertionOperator.Equals) {
    const value = asValue(values[0]);
    if (value === null) return null;
    return {
      fact: assertion.field,
      operator: 'equal',
      value,
    };
  }

  if (assertion.operator === AssertionOperator.NotEquals) {
    const value = asValue(values[0]);
    if (value === null) return null;
    return {
      fact: assertion.field,
      operator: 'notEqual',
      value,
    };
  }

  if (usesRangeOperator(assertion.operator)) {
    if (values.length < 2) return null;
    if (isDateField(assertion.field)) {
      return {
        fact: assertion.field,
        operator:
          assertion.operator === AssertionOperator.BetweenInclusive
            ? 'dateBetweenInclusive'
            : 'dateBetweenExclusive',
        value: [values[0], values[1]],
      };
    }
    const timeRange = isTimeField(assertion.field)
      ? hhmmRangeToMinutes(values[0], values[1])
      : null;
    const left = timeRange ? timeRange[0] : asValue(values[0]);
    const right = timeRange ? timeRange[1] : asValue(values[1]);
    if (left === null || right === null) return null;
    return {
      fact: assertion.field,
      operator: assertion.operator,
      value: [left, right],
    };
  }

  if (assertion.operator === AssertionOperator.DateOnOrAfter) {
    return {
      fact: assertion.field,
      operator: 'dateOnOrAfter',
      value: values[0],
    };
  }

  if (assertion.operator === AssertionOperator.DateOnOrBefore) {
    return {
      fact: assertion.field,
      operator: 'dateOnOrBefore',
      value: values[0],
    };
  }

  return {
    fact: assertion.field,
    operator: assertion.operator,
    value: values.map((raw) => asValue(raw)).filter((value) => value !== null),
  };
};

export const buildValidationFailureCondition = (
  assertion: AssertionRow,
  exactTariffValues: string[] = []
): RuleCondition | null => {
  if (assertion.field === AssertionField.DuplicateClaim) {
    return { fact: 'hasPriorDuplicateClaim', operator: 'equal', value: true };
  }
  if (assertion.field === AssertionField.TariffBillingHistory) {
    return buildBillingHistoryCondition(
      exactTariffValues,
      assertion.billingHistory
    );
  }

  if (assertion.operator === AssertionOperator.MustExist) {
    const missingValueCondition = {
      fact: assertion.field,
      operator: 'mustExist',
      value: false,
    };

    if (assertion.field === AssertionField.ReferringProvider) {
      return {
        any: [
          missingValueCondition,
          {
            fact: assertion.field,
            operator: 'equal',
            value: '00000',
          },
        ],
      };
    }

    return missingValueCondition;
  }

  const matchingCondition = buildAssertionCondition(assertion);
  if (!matchingCondition) return null;

  if (isDemographicAssertionField(assertion.field)) {
    return {
      any: [
        {
          fact: assertion.field,
          operator: 'mustExist',
          value: false,
        },
        negateCondition(matchingCondition),
      ],
    };
  }

  if (
    'operator' in matchingCondition &&
    matchingCondition.operator === 'includesIgnoreCase'
  ) {
    return negateCondition(matchingCondition);
  }

  if ('operator' in matchingCondition) {
    const operator = matchingCondition.operator;
    const invertedOperators: Record<string, string> = {
      equal: 'notEqual',
      notEqual: 'equal',
      [AssertionOperator.In]: AssertionOperator.NotIn,
      [AssertionOperator.NotIn]: AssertionOperator.In,
      [AssertionOperator.LessThan]: AssertionOperator.GreaterThanInclusive,
      [AssertionOperator.LessThanInclusive]: AssertionOperator.GreaterThan,
      [AssertionOperator.GreaterThan]: AssertionOperator.LessThanInclusive,
      [AssertionOperator.GreaterThanInclusive]: AssertionOperator.LessThan,
      [AssertionOperator.BetweenInclusive]:
        AssertionOperator.NotBetweenInclusive,
      [AssertionOperator.NotBetweenInclusive]:
        AssertionOperator.BetweenInclusive,
      dateOnOrAfter: 'dateOnOrBefore',
      dateOnOrBefore: 'dateOnOrAfter',
    };
    const invertedOperator =
      typeof operator === 'string'
        ? invertedOperators[operator] || operator
        : operator;

    return {
      ...matchingCondition,
      operator: invertedOperator,
    };
  }

  return null;
};

// Criteria descriptions

const formatRangeValue = (
  start: string,
  end: string,
  translate: RuleTranslate
): string =>
  `${start || '...'} ${translate('pages:rules.editControls.and')} ${
    end || '...'
  }`;

/** Formats claim values without changing literal text, option labels, or site-local date context. */
const formatClaimCriteriaValue = (
  clause: WhenClause,
  input: ClaimCriteriaInput,
  translate: RuleTranslate,
  timezone: string
): string => {
  if (input === 'text') return clause.scopeValuesText.trim() || '...';

  if (input === 'date' || input === 'number') {
    const values = clause.scopeValuesText.split(',');
    const label = isWhenRangeOperator(clause.whenOperator)
      ? formatRangeValue(values[0], values[1], translate)
      : values[0] || '...';
    return input === 'date' ? `${label} (${timezone})` : label;
  }

  const options = getRuleClaimFieldOptions(clause.scopeMode, translate) ?? [];
  const labels = parseValueList(clause.scopeValuesText).map(
    (value) =>
      options.find((option) => getRuleClaimOptionValue(option) === value)
        ?.label ?? value
  );
  return labels.join(', ') || '...';
};

const formatPatientAgeValue = (
  clause: WhenClause,
  translate: RuleTranslate
): string => {
  const comparison = translate(
    `pages:rules.createModal.patientAgeOperators.${clause.patientAgeOperator}`
  );
  const value =
    clause.patientAgeOperator === PatientAgeOperator.BetweenInclusive
      ? formatRangeValue(
          clause.patientAgeStart,
          clause.patientAgeEnd,
          translate
        )
      : clause.patientAgeValue || '...';
  return `${comparison.toLowerCase()} ${value}`;
};

const formatServiceTimeValue = (
  clause: WhenClause,
  translate: RuleTranslate
): string => {
  if (clause.serviceStartTimeOperator === ServiceStartTimeOperator.Equal)
    return clause.serviceStartTimeValue || '...';
  const comparisonKey =
    clause.serviceStartTimeOperator ===
    ServiceStartTimeOperator.NotBetweenInclusive
      ? 'notBetweenSummary'
      : 'betweenSummary';
  return `${translate(
    `pages:rules.editControls.${comparisonKey}`
  )} ${formatRangeValue(
    clause.serviceStartTimeStart,
    clause.serviceStartTimeEnd,
    translate
  )}`;
};

const formatServiceDayValue = (
  clause: WhenClause,
  translate: RuleTranslate
): string => {
  if (clause.serviceStartDayOperator === ServiceStartDayOperator.Equal)
    return translate(`pages:rules.editDays.${clause.serviceStartDayValue}`);
  const comparisonKey =
    clause.serviceStartDayOperator ===
    ServiceStartDayOperator.NotBetweenInclusive
      ? 'notBetweenSummary'
      : 'betweenSummary';
  const range = formatRangeValue(
    translate(`pages:rules.editDays.${clause.serviceStartDayStart}`),
    translate(`pages:rules.editDays.${clause.serviceStartDayEnd}`),
    translate
  );
  return `${translate(`pages:rules.editControls.${comparisonKey}`)} ${range}`;
};

const formatBillingHistoryPeriod = (
  history: BillingHistoryState,
  translate: RuleTranslate
): string => {
  const option = billingHistoryPeriodOptions.find(
    (period) => period.value === getBillingHistoryPeriod(history)
  );
  if (option)
    return translate(`pages:rules.historyCriteria.${option.labelKey}`);
  return `${translate('pages:rules.historyCriteria.last')} ${
    history.amount || '...'
  } ${translate(
    `pages:rules.createModal.billingHistory.${history.unit}`
  ).toLowerCase()}`;
};

const formatBillingHistoryCriterion = (
  clause: WhenClause,
  translate: RuleTranslate
): string => {
  const provider = clause.billingHistory.sameProvider
    ? ` ${translate('pages:rules.historyCriteria.bySameProvider')}`
    : '';
  return `${translate('pages:rules.historyCriteria.tariff')} ${
    parseValueList(clause.scopeValuesText).join(', ') || '...'
  } ${translate(
    'pages:rules.historyCriteria.alreadyBilled'
  )} ${formatBillingHistoryPeriod(
    clause.billingHistory,
    translate
  )}${provider}`;
};

/** Dispatches each criterion to its value formatter using the builder's locale and timezone. */
const formatWhenClauseValue = (
  clause: WhenClause,
  builder: RuleSummaryContext
): string => {
  const { translate, getDemographicOptions } = builder;
  if (clause.scopeMode === ScopeMode.DuplicateClaim) {
    return translate('pages:rules.historyCriteria.duplicateSummary');
  }
  if (clause.scopeMode === ScopeMode.TariffBillingHistory) {
    return formatBillingHistoryCriterion(clause, translate);
  }
  const fieldLabel = translate(
    `pages:rules.createModal.scopeModes.${clause.scopeMode}`
  );
  const operatorLabel = translate(
    `pages:rules.editWhenOperators.${clause.whenOperator}`
  );

  if (clause.whenOperator === 'exists' || clause.whenOperator === 'notExists')
    return `${fieldLabel} ${operatorLabel}`;

  const claimField = getClaimCriteriaField(clause.scopeMode);
  if (claimField)
    return `${fieldLabel} ${operatorLabel} ${formatClaimCriteriaValue(
      clause,
      claimField.input,
      translate,
      builder.timezone
    )}`;

  if (clause.scopeMode === ScopeMode.PatientAge)
    return `${fieldLabel} ${operatorLabel} ${formatPatientAgeValue(
      clause,
      translate
    )}`;

  if (categoricalDemographicScopeModes.has(clause.scopeMode)) {
    const options = getDemographicOptions(clause.scopeMode);
    const labels = parseValueList(clause.scopeValuesText).map(
      (value) =>
        options.find((option) => option.value === value)?.label || value
    );
    return `${fieldLabel} ${operatorLabel} ${labels.join(', ') || '...'}`;
  }

  if (tariffScopeModes.has(clause.scopeMode)) {
    return `${fieldLabel} ${operatorLabel} ${translate(
      'pages:rules.editControls.oneOf'
    )} ${clause.scopeValuesText || '...'}`;
  }

  if (clause.scopeMode === ScopeMode.ServiceStartTimeLocal)
    return `${translate(
      'pages:rules.editControls.serviceStartTime'
    )} ${operatorLabel} ${formatServiceTimeValue(clause, translate)}`;
  if (clause.scopeMode === ScopeMode.ServiceStartDayLocal)
    return `${translate(
      'pages:rules.editControls.serviceStartDay'
    )} ${operatorLabel} ${formatServiceDayValue(clause, translate)}`;

  return fieldLabel;
};

/** Appends exclusions to the matching condition without changing the surrounding groups. */
export const formatWhenClause = (
  clause: WhenClause,
  builder: RuleSummaryContext
): string => {
  const description = formatWhenClauseValue(clause, builder);
  if (
    !supportsTariffExclusions(clause.scopeMode, clause.whenOperator) ||
    !clause.excludedTariffValuesText?.trim()
  )
    return description;
  return `${description} (${builder.translate(
    'pages:rules.groups.exceptTariffs'
  )}: ${clause.excludedTariffValuesText})`;
};

// Legacy validation descriptions

const formatAssertionValues = (
  assertion: AssertionRow,
  builder: RuleSummaryContext
): string => {
  if (!isCategoricalDemographicAssertionField(assertion.field))
    return assertion.valuesText.trim() || '...';
  const options = builder.getAssertionDemographicOptions(assertion.field);
  return (
    parseValueList(assertion.valuesText)
      .map(
        (value) =>
          options.find((option) => option.value === value)?.label || value
      )
      .join(', ') || '...'
  );
};

const formatAssertionForSummary = (
  assertion: AssertionRow,
  builder: RuleSummaryContext
): string => {
  const { translate } = builder;
  const fieldLabel = translate(
    `pages:rules.createModal.assertionFields.${assertion.field}`
  );
  if (assertion.operator === AssertionOperator.MustExist)
    return `${fieldLabel} ${translate('pages:rules.editControls.mustExist')}`;

  const operatorKey = assertionSummaryOperatorKeys[assertion.operator];
  const operatorLabel = operatorKey
    ? translate(operatorKey)
    : translate(
        `pages:rules.createModal.assertionOperators.${assertion.operator}`
      ).toLowerCase();
  return `${fieldLabel} ${operatorLabel} ${formatAssertionValues(
    assertion,
    builder
  )}`;
};

const formatBillingHistorySummary = (
  assertion: AssertionRow,
  builder: RuleSummaryContext
): string | null => {
  const { translate, exactTariffValues } = builder;
  if (exactTariffValues.length === 0) return null;
  const history = assertion.billingHistory;
  const windowLabel =
    history.windowType === 'calendarDay'
      ? translate('pages:rules.createModal.billingHistory.calendarDay')
      : `${history.amount} ${translate(
          `pages:rules.createModal.billingHistory.${history.unit}`
        ).toLowerCase()}`;
  return `${translate(
    'pages:rules.editControls.tariffs'
  )} ${exactTariffValues.join(', ')} ${translate(
    'pages:rules.editControls.noPriorBilling'
  )} ${windowLabel.toLowerCase()}`;
};

const formatValidationAssertion = (
  assertion: AssertionRow,
  builder: RuleSummaryContext
): string | null => {
  if (assertion.field === AssertionField.DuplicateClaim)
    return builder.translate('pages:rules.editControls.noDuplicate');
  if (assertion.field === AssertionField.TariffBillingHistory)
    return formatBillingHistorySummary(assertion, builder);
  return buildValidationFailureCondition(assertion)
    ? formatAssertionForSummary(assertion, builder)
    : null;
};

export const formatValidationSummary = (
  builder: RuleSummaryContext
): string[] => {
  const prefix =
    builder.severity === RuleSeverity.Block
      ? builder.translate('pages:rules.editControls.stopUnless')
      : builder.translate('pages:rules.editControls.warnUnless');
  return builder.assertions.flatMap((assertion) => {
    const summary = formatValidationAssertion(assertion, builder);
    return summary === null ? [] : [`${prefix} ${summary}`];
  });
};

// Public description helpers

/** Binds formatter inputs once so previews and generated names use the same descriptions. */
export const getRuleSummaryFormatters = (
  builder: RuleSummaryContext
): RuleSummaryFormatters => ({
  formatWhenClause: (clause) => formatWhenClause(clause, builder),
  formatValidationSummary: () => formatValidationSummary(builder),
  localizedApplyFieldLabel: (field) =>
    builder.translate(`pages:rules.editApplyFields.${field}`),
});

export const getRuleCriteriaDescription = (
  builder: RuleSummaryContext
): string => {
  const groups = builder.whenGroups.map((group) => ({
    logic: group.operator,
    rows: group.clauses.map((clause) => ({ clause })),
  }));
  return groups
    .map((group) => {
      const connector = builder.translate(
        group.logic === WhenLogic.Any
          ? 'pages:rules.editControls.or'
          : 'pages:rules.editControls.and'
      );
      const description = group.rows
        .map(({ clause }) => formatWhenClause(clause, builder))
        .join(` ${connector} `);
      return group.rows.length > 1 && groups.length > 1
        ? `(${description})`
        : description;
    })
    .join(
      ` ${builder.translate(
        builder.whenLogic === WhenLogic.Any
          ? 'pages:rules.editControls.or'
          : 'pages:rules.editControls.and'
      )} `
    );
};
